import { DialogContent } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import {
  ReportDataExport,
  ReportExportOptions,
} from '~/scenes/PortalModules/Analytics/common';

import { PersonnelReportTableRow } from '../../usePersonnelReportTables';

import { useSxStyles } from './useStyles';

interface ChartTableProps {
  isVisible: boolean;
  tablePosition?: {
    top: number;
    left: number;
  };
  columns: GridColDef<PersonnelReportTableRow>[];
  rows: PersonnelReportTableRow[];
  label: string | number;
  onClose: () => void;
  exportFileNamePrefix?: string;
}

export const ChartTable = (props: ChartTableProps) => {
  const {
    isVisible,
    tablePosition,
    columns,
    rows,
    label,
    onClose,
    exportFileNamePrefix,
  } = props;

  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const sxStyles = useSxStyles();

  const { title, fileName } = useMemo(() => {
    return {
      title: `${t('laPeople')}: ${label}`,
      fileName: `${
        !!exportFileNamePrefix ? exportFileNamePrefix : t('laPersonelReport')
      } - ${t('laPeople')} - ${label}`,
    };
  }, [t, label, exportFileNamePrefix]);

  if (!isVisible) {
    return null;
  }

  return (
    <DraggableDialog
      open={true}
      onClose={onClose}
      hideBackdrop={true}
      disableBackdropClick={true}
      disableEnforceFocus={true}
      allowClickThroughBackdrop={true}
      sx={{
        left: tablePosition?.left,
        width: 'fit-content',
        height: 'fit-content',
      }}
    >
      <SolaForceDialogTitle
        onClose={onClose}
        actions={
          <ReportDataExport
            title={fileName}
            apiRef={apiRef}
            exportOptions={[ReportExportOptions.Excel, ReportExportOptions.CSV]}
          />
        }
      >
        {title}
      </SolaForceDialogTitle>
      <DialogContent sx={sxStyles.content}>
        <SolaForceDataGrid
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          initialState={{ density: 'compact' }}
          getRowId={(row) => row.employeeId}
          disableColumnSelector={true}
        />
      </DialogContent>
    </DraggableDialog>
  );
};
